import { Component, OnInit } from '@angular/core';
import {
  ICalculatorConfigViewModel,
  ICalculatorSearchView,
  ICalculatorSearchViewModel,
  ISchoolsView,
  ISystemsView
} from 'src/app/shared/models/common';
import { CommonService } from 'src/app/shared/services/common.service';
import { FrameworkService } from 'gadoe-framework';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { faFacebook, faInstagram, faPinterest, faTwitter, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { faRss, faCircle, faIcons, faHome  } from '@fortawesome/free-solid-svg-icons';
import { Config } from 'src/app/shared/config';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-eligibilitycalculatorsearch',
  templateUrl: './eligibilitycalculatorsearch.component.html',
  styleUrls: ['./eligibilitycalculatorsearch.component.css']
})
export class EligibilitycalculatorsearchComponent implements OnInit {
  faFacebook = faFacebook;
  faTwitter = faTwitter;
  faInstagram = faInstagram;
  faYoutube = faYoutube;
  faPinterest = faPinterest;
  faRss = faRss;
  faCircle = faCircle;
  faIcon = faIcons;

  CalcConfig: ICalculatorConfigViewModel = this.createEmptyCalcConfig();
  Systems: ISystemsView[] = [];
  Schools: ISchoolsView[] = [];
  systemOptions: Array<{ text: string; value: string }> = [];
  schoolOptions: Array<{ text: string; value: string }> = [];
  filteredSystems: Array<{ text: string; value: string }> = [];
  filteredSchools: Array<{ text: string; value: string }> = [];
  model: ICalculatorSearchView = this.createEmptySearchModel();
  calcSearch: ICalculatorSearchViewModel = this.createEmptyCalcSearchResult();

  FiscalYears: { text: string; value: number }[] = [];
  defaultFiscalYear = { text: 'Select School Year', value: null as any };
  defaultSystem = { systemName: 'Select System', systemID: null as any };
  defaultSchool = { schoolName: 'Select School', schoolID: null as any };
  datePickerPopupSettings = {
    popupClass: 'eligibility-datepicker-popup'
  };

  showChildform = false;
  noMatchFound = false;
  isInitialLoading = true;
  isSearching = false;
  isSchoolLoading = false;

  // If your API later gives open/closed flag, set this from API response.
  isCalculatorOpen = true;

  noMatchMessage =
    'We were unable to find a match in the scholarship calculator with the information you provided. If you believe that you child should receive a scholarship award for the current school year, please click on the link below. This link will open a document for you to provide more information and will direct you where to send it for further review.';

  calSearchForm!: FormGroup;

  constructor(
    private frameworkService: FrameworkService,
    private commonService: CommonService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.buildForm();
    this.loadInitialData();
  }

  get f(): { [key: string]: AbstractControl } {
    return this.calSearchForm.controls;
  }

  private buildForm(): void {
    this.calSearchForm = this.formBuilder.group({
      fiscalYear: [null, Validators.required],
      system: ['', Validators.required],
      school: ['', Validators.required],
      firstName: ['', [Validators.required, Validators.pattern(/^[A-Za-z .,'-]+$/)]],
      lastName: ['', [Validators.required, Validators.pattern(/^[A-Za-z .,'-]+$/)]],
      dateofBirth: ['', [Validators.required, this.noFutureDateValidator]]
    });
  }

  private loadInitialData(): void {
    this.showChildform = false;
    this.noMatchFound = false;
    this.isInitialLoading = true;
    this.isSearching = false;
    this.isSchoolLoading = false;
    this.isCalculatorOpen = false;

    this.model = this.createEmptySearchModel();
    this.calcSearch = this.createEmptyCalcSearchResult();
    this.CalcConfig = this.createEmptyCalcConfig();
    this.FiscalYears = [];
    this.Systems = [];
    this.Schools = [];
    this.systemOptions = [];
    this.schoolOptions = [];
    this.filteredSystems = [];
    this.filteredSchools = [];

    this.calSearchForm.reset({
      fiscalYear: null,
      system: null,
      school: null,
      firstName: '',
      lastName: '',
      dateofBirth: ''
    });

    forkJoin({
      config: this.commonService.getCalculatorConfig(),
      systems: this.commonService.getSystemsList()
    }).subscribe({
      next: ({ config, systems }) => {
        this.applyCalculatorConfig(config);
        this.applySystems(systems);
      },
      error: (err: any) => {
        this.isInitialLoading = false;
        this.frameworkService.logError(err);
      },
      complete: () => {
        this.isInitialLoading = false;
        this.frameworkService.logInfo('loadInitialData() retrieved calculator config and systems list');
      }
    });
  }

  private noFutureDateValidator(control: AbstractControl) {
    if (!control.value) {
      return null;
    }

    const inputDate = new Date(control.value);
    if (isNaN(inputDate.getTime())) {
      return { invalidDate: true };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    inputDate.setHours(0, 0, 0, 0);

    return inputDate > today ? { futureDate: true } : null;
  }

  private createEmptySearchModel(): ICalculatorSearchView {
    return {
      fiscalYear: 0,
      systemID: '',
      schoolID: '',
      firstName: '',
      lastName: '',
      dateofBirth: new Date()
    };
  }

  private createEmptyCalcSearchResult(): ICalculatorSearchViewModel {
    return {
      birthDate: new Date(),
      displayAmount: 0,
      firstName: '',
      fiscalYear: 0,
      gtid: '',
      lastName: '',
      middleName: '',
      orginalAmount: 0,
      percentage: 0,
      qbeAmount: 0,
      schoolID: '',
      sedAmount: 0,
      systemID: '',
      found: 0,
      currentYear: '',
      previousYear: '',
      contactName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: '',
      message: '',
      systemName: '',
      schoolName: ''
    };
  }

  private createEmptyCalcConfig(): ICalculatorConfigViewModel {
    return {
      fiscalYear: '',
      previousYear: '',
      currentYear: '',
      contactName: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: '',
      message: '',
      isCalculatorOpened: false
    };
  }

  getSchools(systemID: string): void {
    this.Schools = [];
    this.filteredSchools = [];
    this.f['school'].setValue(null);
    this.isSchoolLoading = true;

    this.commonService.getSchoolsList(systemID).subscribe({
      next: (response: ISchoolsView[]) => {
        if (response) {
          this.Schools = [...response].sort((a, b) =>
            (a.schoolName || '').localeCompare(b.schoolName || '')
          );
          this.schoolOptions = this.Schools.map((school) => ({
            text: school.schoolName,
            value: school.schoolID
          }));
          this.filteredSchools = [...this.schoolOptions];
        }
      },
      error: (err: any) => {
        this.isSchoolLoading = false;
        this.frameworkService.logError(err);
      },
      complete: () => {
        this.isSchoolLoading = false;
        this.frameworkService.logInfo('getSchools() retrieved Schools list');
      }
    });
  }

  OnSystemChange(systemID: string): void {
    this.model.systemID = systemID;

    if (systemID) {
      this.getSchools(systemID);
    } else {
      this.Schools = [];
      this.filteredSystems = [...this.systemOptions];
      this.filteredSchools = [];
      this.isSchoolLoading = false;
      this.f['school'].setValue(null);
    }
  }

  submitSearch(): void {
    if (!this.isCalculatorOpen || this.isSearching || this.isInitialLoading) {
      return;
    }

    if (this.calSearchForm.invalid) {
      this.calSearchForm.markAllAsTouched();
      return;
    }

    this.noMatchFound = false;
    this.showChildform = false;
    this.isSearching = true;

    this.model = {
      fiscalYear: +this.f['fiscalYear'].value,
      systemID: this.f['system'].value ?? '',
      schoolID: this.f['school'].value ?? '',
      firstName: this.f['firstName'].value?.trim(),
      lastName: this.f['lastName'].value?.trim(),
      dateofBirth: this.f['dateofBirth'].value
    };

    this.getSearchData(this.model);
  }

  private combineData(): void {
    this.calcSearch.currentYear = this.CalcConfig.currentYear;
    this.calcSearch.previousYear = this.CalcConfig.previousYear;
    this.calcSearch.contactName = this.CalcConfig.contactName;
    this.calcSearch.addressLine1 = this.CalcConfig.addressLine1;
    this.calcSearch.addressLine2 = this.CalcConfig.addressLine2;
    this.calcSearch.city = this.CalcConfig.city;
    this.calcSearch.state = this.CalcConfig.state;
    this.calcSearch.zip = this.CalcConfig.zip;
    this.calcSearch.phone = this.CalcConfig.phone;
    this.calcSearch.email = this.CalcConfig.email;
    this.calcSearch.message = this.CalcConfig.message;
  }

  getSearchData(calcSearch: ICalculatorSearchView): void {
    this.commonService.getCalcSearchData(calcSearch).subscribe({
      next: (response: ICalculatorSearchViewModel[]) => {
        if (response?.length > 0) {
          this.calcSearch = response[0];
          this.combineData();

          if (this.calcSearch.found && this.calcSearch.found > 0) {
            this.showChildform = true;
            this.noMatchFound = false;
          } else {
            this.showChildform = false;
            this.noMatchFound = true;
          }
        } else {
          this.showChildform = false;
          this.noMatchFound = true;
        }
      },
      error: (err: any) => {
        this.isSearching = false;
        this.frameworkService.logError(err);
      },
      complete: () => {
        this.isSearching = false;
        this.frameworkService.logInfo('getSearchData() retrieved search result');
      }
    });
  }

  cancelHandler(): void {
    this.loadInitialData();
  }

  clearClick(): void {
    this.calSearchForm.reset({
      fiscalYear: this.getFiscalYearValue(this.CalcConfig.fiscalYear),
      system: null,
      school: null,
      firstName: '',
      lastName: '',
      dateofBirth: ''
    });

    this.Schools = [];
    this.schoolOptions = [];
    this.filteredSystems = [...this.systemOptions];
    this.filteredSchools = [];
    this.noMatchFound = false;
    this.showChildform = false;
    this.isSearching = false;
    this.isSchoolLoading = false;
    this.model = this.createEmptySearchModel();
  }

  handleSystemFilter(value: string): void {
    this.filteredSystems = this.filterOptions(this.systemOptions, value);
  }

  handleSchoolFilter(value: string): void {
    this.filteredSchools = this.filterOptions(this.schoolOptions, value);
  }

  get inquiryFormUrl(): string {
    return Config.calculatorInquiryFormUrl || 'https://gadoesa.jotform.com/261964233128862';
  }

  private getFiscalYearValue(fiscalYear: string): number | null {
    const parsedValue = Number(fiscalYear);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
  }

  private applyCalculatorConfig(response: ICalculatorConfigViewModel): void {
    if (!response) {
      return;
    }

    this.CalcConfig = response;
    this.isCalculatorOpen = !!response.isCalculatorOpened;
    this.f['fiscalYear'].setValue(this.getFiscalYearValue(response.fiscalYear));
  }

  private applySystems(response: ISystemsView[]): void {
    if (!response) {
      return;
    }

    this.Systems = [...response].sort((a, b) =>
      (a.systemName || '').localeCompare(b.systemName || '')
    );
    this.systemOptions = this.Systems.map((system) => ({
      text: system.systemName,
      value: system.systemID
    }));
    this.filteredSystems = [...this.systemOptions];
  }

  private filterOptions(items: Array<{ text: string; value: string }>, value: string): Array<{ text: string; value: string }> {
    const term = value?.toLowerCase().trim() ?? '';

    if (!term) {
      return [...items];
    }

    return items.filter(item => item.text.toLowerCase().includes(term));
  }

}
